import { SelectOptionType, addToast } from '@octano/global-ui';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'reactstrap';

import IdentificationTypeLoader, {
  useIdentificationTypeLoader,
} from '../../../components/loaders/IdentificationTypeLoader';
import SectionTitle from '../../../components/text/SectionTitle';
import { useParameters } from '../../../hooks/useParameters';
import { downloadFromBlob } from '../../../utils/blob';
import { getStudyPlansForSelect } from '../CurrentAccount/StudentList/api';

import { downloadAgreedPayments, listAgreedPayments } from './api';
import { AgreedPaymentsTable } from './parts/PaymentsTable';
import { SearchControls } from './parts/SearchControls';
import { AgreedPaymentRow, AgreedPaymentsFilters } from './types';

const dictPrefix = 'financialPortal.agreedPayments';
const ITEMS_PER_PAGE = 10;

function AgreedPaymentsContent(): ReactElement {
  const { t } = useTranslation();
  const { data: identificationTypeData } = useIdentificationTypeLoader();

  const identificationTypes = useMemo<SelectOptionType[]>(
    () =>
      (identificationTypeData?.identificationTypes ?? []).map((it) => ({
        value: it.id,
        label: it.name,
      })),
    [identificationTypeData],
  );
  const [programs, setPrograms] = useState<SelectOptionType[]>([]);
  const [data, setData] = useState<AgreedPaymentRow[]>([]);
  const [filters, setFilters] = useState<AgreedPaymentsFilters>({});
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPayments, setTotalPayments] = useState(0);

  const search = useCallback(
    async (sp: AgreedPaymentsFilters, p: number) => {
      setLoading(true);
      const res = await listAgreedPayments({
        ...sp,
        page: p,
        items_per_page: ITEMS_PER_PAGE,
      });
      if (res.error) {
        addToast({
          color: 'danger',
          icon: 'error',
          text: t(`${dictPrefix}.errorOnSearch`),
        });
      } else {
        setData(res.data?.data ?? []);
        setTotalPages(res.data?.total_pages ?? 0);
        setTotalPayments(res.data?.total ?? 0);
      }
      setLoading(false);
    },
    [t],
  );

  const handleDownload = useCallback(async () => {
    setDownloadLoading(true);
    const res = await downloadAgreedPayments(filters);
    if (res.status === 204) {
      addToast({
        color: 'warning',
        icon: 'warning',
        text: t(`${dictPrefix}.generatingFile.empty`),
      });
    } else if (res.error) {
      addToast({
        color: 'danger',
        icon: 'error',
        text: t(`${dictPrefix}.generatingFile.errorOnDownload`),
      });
    } else if (res.data) {
      downloadFromBlob(
        new Blob([res.data], { type: 'vnd.ms-excel' }),
        `pagos-acordados-${Date.now()}.xls`,
      );
      addToast({
        color: 'success',
        icon: 'success',
        text: t(`${dictPrefix}.generatingFile.download`),
      });
    }
    setDownloadLoading(false);
  }, [filters, t]);

  const handleClear = useCallback(() => {
    setFilters({});
    setPage(0);
  }, []);

  const hasFilters = useMemo(
    () =>
      Boolean(
        filters.name ||
          filters.identificationTypeId ||
          filters.identification ||
          filters.studyPlanId,
      ),
    [filters],
  );

  useEffect(() => {
    search(filters, page);
  }, [search, page, filters]);

  useEffect(() => {
    let mounted = true;
    getStudyPlansForSelect().then((result) => {
      if (!mounted) {
        return;
      }
      if (result.data) {
        setPrograms(
          result.data.data.map((sp) => ({ value: sp.id, label: sp.name })),
        );
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Card className="mt-5 mx-3 p-4">
      <SectionTitle text={t(`${dictPrefix}.title`)} />
      <SearchControls
        identificationTypes={identificationTypes}
        programs={programs}
        isDownloadLoading={downloadLoading}
        onSearch={(f) => {
          setPage(0);
          setFilters(f);
        }}
        onClear={handleClear}
        onDownload={handleDownload}
      />
      <AgreedPaymentsTable
        data={data}
        isLoadingResults={loading}
        totalPayments={totalPayments}
        totalPages={totalPages}
        currentPage={page}
        itemsPerPage={ITEMS_PER_PAGE}
        hasFilters={hasFilters}
        onChangePage={setPage}
      />
    </Card>
  );
}

export default function AgreedPayments(): ReactElement {
  const { defaultCountry } = useParameters();

  return (
    <IdentificationTypeLoader countryId={defaultCountry ?? null}>
      <AgreedPaymentsContent />
    </IdentificationTypeLoader>
  );
}
